import type { BookingWizardState } from "./useBookingDraft.js";
import type { BuildingDetails, BuildingType } from "../../shared/types.js";
import {
  BuildingIcon,
  RowhouseIcon,
  SemiDetachedIcon,
  DetachedIcon,
  OtherBuildingIcon,
  CheckIcon,
} from "../../components/Icons.js";

const OPTIONS: { value: BuildingType; label: string; icon: any }[] = [
  { value: "appartement", label: "Appartement", icon: <BuildingIcon size={26} /> },
  { value: "rijwoning", label: "Rijwoning", icon: <RowhouseIcon size={26} /> },
  { value: "halfopen_woning", label: "Halfopen woning", icon: <SemiDetachedIcon size={26} /> },
  { value: "open_bebouwing", label: "Open bebouwing", icon: <DetachedIcon size={26} /> },
  { value: "ander_gebouw", label: "Ander gebouw", icon: <OtherBuildingIcon size={26} /> },
];

export function BuildingSelector({
  building,
  onChange,
}: {
  building: BuildingDetails;
  onChange: (b: BuildingDetails) => void;
}) {
  function patch(p: Partial<BuildingDetails>) {
    onChange({ ...building, ...p });
  }

  return (
    <div>
      <h2>Wat voor gebouw wilt u laten keuren?</h2>
      <p className="text-small text-muted">Kies het type gebouw — dit bepaalt mee de basisprijs en de aanpak van de inspectie.</p>

      <div className="option-grid" role="group" aria-label="Type gebouw">
        {OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className="option-card"
            aria-pressed={building.buildingType === opt.value}
            onClick={() => patch({ buildingType: opt.value })}
          >
            <span className="option-card__check">
              <CheckIcon size={12} />
            </span>
            {opt.icon}
            {opt.label}
          </button>
        ))}
      </div>

      <div className="form-row mt-6">
        <div className="form-field">
          <label htmlFor="floors">Aantal bouwlagen (incl. gelijkvloers)</label>
          <select
            id="floors"
            className="form-select"
            value={building.floors}
            onChange={(e: any) => patch({ floors: Number(e.target.value) })}
          >
            {Array.from({ length: 6 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n} {n === 1 ? "bouwlaag" : "bouwlagen"}
              </option>
            ))}
          </select>
          <p className="form-hint">
            Boven het aantal woonlagen dat in de basisprijs van uw gebouwtype is inbegrepen, geldt een toeslag per
            extra bouwlaag.
          </p>
        </div>
        <div className="form-field">
          <label htmlFor="buildYear">Bouwjaar (ongeveer)</label>
          <input
            id="buildYear"
            type="number"
            inputMode="numeric"
            className="form-input"
            min={1800}
            max={new Date().getFullYear()}
            value={building.buildYear}
            onChange={(e: any) => patch({ buildYear: Number(e.target.value) })}
          />
          <p className="form-hint">Bij benadering is voldoende — bepaalt mee het risicoprofiel.</p>
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label htmlFor="surfaceM2">Totale oppervlakte (m²)</label>
          <input
            id="surfaceM2"
            type="number"
            inputMode="numeric"
            className="form-input"
            min={10}
            max={5000}
            value={building.surfaceM2}
            onChange={(e: any) => patch({ surfaceM2: Number(e.target.value) })}
          />
        </div>
        <div className="form-field">
          <label htmlFor="units">Aantal wooneenheden</label>
          <input
            id="units"
            type="number"
            inputMode="numeric"
            className="form-input"
            min={1}
            max={500}
            value={building.units}
            onChange={(e: any) => patch({ units: Number(e.target.value) })}
          />
          <p className="form-hint">
            Meer dan 1 eenheid (bv. een appartementsgebouw met gemene delen)? Dan werken we via een offerte op maat
            — zie <a href="/vme-en-syndici">VME &amp; Syndici</a>.
          </p>
        </div>
      </div>

      <div className="form-row">
        <fieldset className="form-field" style={{ border: "none", padding: 0, margin: 0 }}>
          <legend className="form-label">Kelder aanwezig?</legend>
          <div className="flex gap-2 mt-2">
            <button type="button" className="option-card" aria-pressed={building.hasBasement} onClick={() => patch({ hasBasement: true })} style={{ minHeight: 44 }}>
              Ja
            </button>
            <button type="button" className="option-card" aria-pressed={!building.hasBasement} onClick={() => patch({ hasBasement: false })} style={{ minHeight: 44 }}>
              Nee
            </button>
          </div>
        </fieldset>
        <fieldset className="form-field" style={{ border: "none", padding: 0, margin: 0 }}>
          <legend className="form-label">Bijgebouwen aanwezig?</legend>
          <div className="flex gap-2 mt-2">
            <button type="button" className="option-card" aria-pressed={building.hasOutbuildings} onClick={() => patch({ hasOutbuildings: true })} style={{ minHeight: 44 }}>
              Ja
            </button>
            <button type="button" className="option-card" aria-pressed={!building.hasOutbuildings} onClick={() => patch({ hasOutbuildings: false })} style={{ minHeight: 44 }}>
              Nee
            </button>
          </div>
        </fieldset>
      </div>

      <div className="form-field">
        <label htmlFor="notes">Aanvullende opmerkingen (optioneel)</label>
        <textarea
          id="notes"
          className="form-textarea"
          rows={3}
          value={building.notes || ""}
          onChange={(e: any) => patch({ notes: e.target.value })}
          placeholder="Bv. beperkte toegankelijkheid, specifieke ruimtes, ..."
        />
      </div>
    </div>
  );
}
